import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useToast } from '@/hooks/use-toast';
import { pb } from '@/lib/pocketbase';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { adminNotificationService } from '@/services/adminNotificationService';

interface ProfileSuspensionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type RequestType = 'suspend' | 'delete';
type Reason = 'taking_break' | 'privacy_concerns' | 'found_alternative' | 'not_satisfied' | 'too_expensive' | 'technical_issues' | 'other';

const ProfileSuspensionModal = ({ isOpen, onClose }: ProfileSuspensionModalProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [requestType, setRequestType] = useState<RequestType | null>(null);
  const [reason, setReason] = useState<Reason | null>(null);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reasonOptions = [
    { value: 'taking_break', label: 'Taking a break from the platform' },
    { value: 'privacy_concerns', label: 'Privacy concerns' },
    { value: 'found_alternative', label: 'Found alternative solution' },
    { value: 'not_satisfied', label: 'Not satisfied with service' },
    { value: 'too_expensive', label: 'Too expensive' },
    { value: 'technical_issues', label: 'Technical issues' },
    { value: 'other', label: 'Other reason' }
  ];

  const handleReset = () => {
    setStep(1);
    setRequestType(null);
    setReason(null);
    setAdditionalNotes('');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!user || !requestType || !reason) {
      toast({
        title: 'Error',
        description: 'Please complete all required fields',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Create suspension request in PocketBase
      const request = await pb.collection('profile_suspension_requests').create({
        user_id: user.id,
        user_email: user.email,
        request_type: requestType,
        reason: reason,
        additional_notes: additionalNotes,
        status: 'pending'
      });

      // Send notification to all admins using the notification service
      try {
        const reasonLabels: Record<Reason, string> = {
          'taking_break': 'Taking a break',
          'privacy_concerns': 'Privacy concerns',
          'found_alternative': 'Found alternative',
          'not_satisfied': 'Not satisfied',
          'too_expensive': 'Too expensive',
          'technical_issues': 'Technical issues',
          'other': 'Other reason'
        };

        await adminNotificationService.notifyProfileSuspensionRequest(
          request.id,
          user.name || '',
          user.email,
          requestType,
          reasonLabels[reason]
        );
      } catch (notifError) {
        console.error('Error sending admin notifications:', notifError);
        // Continue even if notifications fail
      }

      toast({
        title: 'Request Submitted',
        description: `Your ${requestType === 'suspend' ? 'suspension' : 'deletion'} request has been submitted and will be reviewed by an administrator.`
      });

      handleClose();
    } catch (error) {
      console.error('Error submitting suspension request:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] z-[102]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-warning" />
            Profile Management
          </DialogTitle>
          <DialogDescription>
            {step === 1 && 'Choose to suspend or delete your profile'}
            {step === 2 && 'Tell us why you\'re leaving'}
            {step === 3 && 'Any additional details? (Optional)'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Step 1: Choose Request Type */}
          {step === 1 && (
            <RadioGroup value={requestType || ''} onValueChange={(value) => setRequestType(value as RequestType)}>
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent cursor-pointer">
                  <RadioGroupItem value="suspend" id="suspend" />
                  <div className="flex-1">
                    <Label htmlFor="suspend" className="cursor-pointer font-semibold">
                      Suspend Profile
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Temporarily hide your profile. You can reactivate it anytime.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-4 border rounded-lg hover:bg-accent cursor-pointer">
                  <RadioGroupItem value="delete" id="delete" />
                  <div className="flex-1">
                    <Label htmlFor="delete" className="cursor-pointer font-semibold">
                      Delete Profile
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Permanently remove your profile and data. This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
            </RadioGroup>
          )}

          {/* Step 2: Choose Reason */}
          {step === 2 && (
            <RadioGroup value={reason || ''} onValueChange={(value) => setReason(value as Reason)}>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {reasonOptions.map((option) => (
                  <div key={option.value} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <RadioGroupItem value={option.value} id={option.value} />
                    <Label htmlFor={option.value} className="cursor-pointer flex-1">
                      {option.label}
                    </Label>
                  </div>
                ))}
              </div>
            </RadioGroup>
          )}

          {/* Step 3: Additional Notes */}
          {step === 3 && (
            <div className="space-y-2">
              <Label htmlFor="notes">Additional Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Tell us more about your decision..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                rows={6}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(step - 1)} disabled={isSubmitting}>
              Back
            </Button>
          )}
          
          {step < 3 ? (
            <Button 
              onClick={() => setStep(step + 1)} 
              disabled={step === 1 ? !requestType : !reason}
            >
              Continue
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Request
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSuspensionModal;
